import React, { FC, useEffect, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import axiosInstant from '../../../api';
import { sendOptInNotifications as sendOptInNotificationsUrl } from '../../../api/endpoints';
import { Button } from '../../../components/Button/Button';
import { Pro } from './ExpertSelectedCard';
import { ExpertConfirmCard } from './ExpertConfirmCard';
import { getBpp, getProDecision, isProDecided, describeSharedSiblings } from '../helper';

interface IOptInNotificationsModal {
  open: boolean;
  onClose: () => void;
  pros: Pro[];
  brandId?: number;
  projectId?: number;
  onSent?: () => void;
  /** Other projects sharing the selection process (for a shared-scope note). */
  sharedSiblingProjects?: { id: number; title?: string }[];
}

export const OptInNotificationsModal: FC<IOptInNotificationsModal> = ({ open, onClose, pros, brandId, projectId, onSent, sharedSiblingProjects = [] }) => {
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSelectedIds([]);
  }, [open]);

  const toggle = (userid: number | string) => {
    setSelectedIds((prev) => prev.includes(userid) ? prev.filter((id) => id !== userid) : [...prev, userid]);
  };

  const canSubmit = !submitting && selectedIds.length > 0 && !!brandId && !!projectId;

  const handleSend = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await axiosInstant.post(
        sendOptInNotificationsUrl(brandId!, projectId!),
        { user_ids: selectedIds.map((id) => Number(id)) },
      );
      const notified = data?.notified ?? 0;
      const followedUp = data?.followedUp ?? 0;
      const skipped = data?.skipped ?? 0;
      const muted = data?.muted ?? 0;
      const expertWord = (n: number) => `${n} expert${n === 1 ? '' : 's'}`;
      const pitched = notified + followedUp;
      if (pitched > 0) {
        toast.success(
          skipped > 0
            ? `${expertWord(pitched)} pitched. ${expertWord(skipped)} already responded.`
            : `${expertWord(pitched)} pitched.`,
          { duration: 4000 },
        );
      } else {
        toast(
          skipped > 0
            ? `No notifications sent. ${expertWord(skipped)} already responded.`
            : 'No notifications sent.',
          { duration: 4000 },
        );
      }
      if (muted > 0) {
        toast.error(`${expertWord(muted)} turned pitch notifications off, so they were not contacted.`);
      }
      onSent?.();
      onClose();
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (apiError === 'ExpertRatesRequired') {
        toast.error('Set a rate for every proposed expert before sending opt-in notifications.');
      } else if (apiError === 'ProjectNotPitchEligible') {
        toast.error('Experts can be pitched once the project reaches Expert Proposals.');
      } else {
        toast.error(apiError || 'Failed to send notifications');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxWidth: 720, borderRadius: '20px' } }}>
      <DialogTitle className="flex justify-between items-center">
        <Typography variant="h5">Send Opt In / Out Notifications</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent className="border-y border-border-token !p-0">
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 320, maxHeight: 520 }}>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
            {pros.length === 0 && (
              <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'var(--text-2)' }}>No experts have been added to this project yet.</Typography>
            )}
            {pros.length > 0 && (
              <Typography variant="body2" sx={{ color: 'var(--text-2)', fontSize: 12.5, mb: 1.5 }}>
                Opted-in / opted-out experts are locked. Already-notified experts can be re-selected to send a follow-up reminder.
              </Typography>
            )}
            {pros.length > 0 && sharedSiblingProjects.length > 0 && (
              <Typography variant="body2" sx={{ color: 'var(--accent-text)', fontSize: 12.5, mb: 1.5 }}>
                These experts are shared with {describeSharedSiblings(sharedSiblingProjects)}, so this notification covers them too.
              </Typography>
            )}
            {pros.map((expert) => {
              const bpp = getBpp(expert);
              return (
                <ExpertConfirmCard
                  key={expert.userid}
                  expert={expert}
                  isSelected={selectedIds.includes(expert.userid)}
                  isLocked={isProDecided(bpp)}
                  decision={getProDecision(bpp)}
                  onToggle={() => toggle(expert.userid)}
                />
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid var(--border)' }}>
        <Typography sx={{ flex: 1, fontSize: 14, color: 'var(--text-2)' }}>
          {selectedIds.length > 0 && <strong style={{ color: 'var(--text)' }}>{selectedIds.length}</strong>}
          {selectedIds.length > 0 ? ` Expert${selectedIds.length > 1 ? 's' : ''} Selected.` : 'Select at least one expert.'}
        </Typography>
        <Button onClick={handleSend} disabled={!canSubmit} size="small"
          className={classNames('!py-0 !px-5 h-[44px] !rounded-xl', { '!bg-tint !text-muted': !canSubmit })}>
          {submitting ? 'Sending...' : 'Send Notifications'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptInNotificationsModal;
